import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, UserIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/ui/components/atoms/Button';
import { useEligibleResponsables, useChangeResponsable } from '../hooks/useAffaireSettings';
import { getWorkloadStatistics } from '../api/workload.api';
import type { AffaireSettings } from '../types/tracker.types';

interface ChangeResponsableModalProps {
  isOpen: boolean;
  onClose: () => void;
  affaireId: number;
  currentSettings: AffaireSettings | null;
  responsableLine: 'primary' | 'second' | 'third';
  onSuccess: () => void;
}

export const ChangeResponsableModal: React.FC<ChangeResponsableModalProps> = ({
  isOpen,
  onClose,
  affaireId,
  currentSettings,
  responsableLine,
  onSuccess,
}) => {
  const { eligibleUsers, isLoading: isLoadingUsers } = useEligibleResponsables();
  const { changeResponsable, isChanging, error: changeError } = useChangeResponsable();

  // Fetch workload stats to show active dossier count per user
  const { data: workloadData } = useQuery({
    queryKey: ['workload-stats'],
    queryFn: getWorkloadStatistics,
    enabled: isOpen,
    staleTime: 30_000,
  });

  // Build a userId -> active affaire count map
  const workloadMap = useMemo(() => {
    const map: Record<string, number> = {};
    if (workloadData?.responsables) {
      for (const r of workloadData.responsables) {
        map[r.id] = r.current_active_affaire_count;
      }
    }
    return map;
  }, [workloadData]);

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Get current responsable based on line
  const getCurrentResponsable = () => {
    if (!currentSettings) return null;
    switch (responsableLine) {
      case 'primary':
        return currentSettings.primary_responsable;
      case 'second':
        return currentSettings.second_line_responsable;
      case 'third':
        return currentSettings.third_line_responsable;
      default:
        return null;
    }
  };

  const currentResponsable = getCurrentResponsable();

  useEffect(() => {
    if (currentResponsable?.id) {
      setSelectedUserId(currentResponsable.id);
    } else {
      setSelectedUserId('');
    }
  }, [currentResponsable, responsableLine]);

  const handleSave = async () => {
    if (!selectedUserId) {
      alert('Veuillez sélectionner un responsable');
      return;
    }

    const changed = await changeResponsable(affaireId, {
      responsable_user_id: selectedUserId,
      responsable_line: responsableLine,
      notes: notes.trim() || undefined,
    });

    if (changed) {
      setNotes('');
      onSuccess();
      onClose();
    }
  };

  const handleClose = () => {
    setNotes('');
    onClose();
  };

  if (!isOpen) return null;

  const formatUserName = (user: any) => {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Utilisateur inconnu';
  };

  const getLineLabel = () => {
    switch (responsableLine) {
      case 'primary':
        return '1ère ligne (Principal)';
      case 'second':
        return '2ème ligne (RV)';
      case 'third':
        return '3ème ligne (Support)';
      default:
        return 'Responsable';
    }
  };

  const getLineDescription = () => {
    switch (responsableLine) {
      case 'primary':
        return 'Le responsable principal doit avoir le rôle "bureau" ou "admin"';
      case 'second':
        return 'Le responsable de 2ème ligne (RV) peut être synchronisé automatiquement ou changé manuellement';
      case 'third':
        return 'Le responsable de 3ème ligne peut être assigné à tout utilisateur';
      default:
        return '';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="tracker-modal-backdrop" onClick={handleClose} />

      {/* Modal */}
      <div className="tracker-modal">
        <div className="tracker-modal-header">
          <h3 className="tracker-modal-title">
            <UserIcon className="w-5 h-5" />
            <span>Changer le responsable - {getLineLabel()}</span>
          </h3>
          <button
            className="tracker-modal-close"
            onClick={handleClose}
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="tracker-modal-content">
          {isLoadingUsers ? (
            <div className="text-center py-8 text-gray-500">
              Chargement des responsables éligibles...
            </div>
          ) : (
            <div className="space-y-4">
              {/* Line Description */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-700">
                  {getLineDescription()}
                </p>
              </div>

              {/* Current Responsable Info */}
              {currentResponsable?.id && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900 mb-1">
                    Responsable actuel
                  </p>
                  <p className="text-sm text-blue-700">
                    {formatUserName(currentResponsable)}
                  </p>
                  {currentResponsable.email && (
                    <p className="text-xs text-blue-600 mt-1">
                      {currentResponsable.email}
                    </p>
                  )}
                  {currentResponsable.role && (
                    <p className="text-xs text-blue-600 mt-1">
                      Rôle: {currentResponsable.role}
                    </p>
                  )}
                </div>
              )}

              {/* Select New Responsable */}
              <div>
                <label htmlFor="responsable-select" className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau responsable <span className="text-red-500">*</span>
                </label>
                <select
                  id="responsable-select"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isChanging}
                >
                  <option value="">-- Sélectionner un responsable --</option>
                  {eligibleUsers.map((user) => {
                    const count = workloadMap[user.id];
                    const workloadLabel = count !== undefined ? ` — ${count} dossier${count !== 1 ? 's' : ''} actif${count !== 1 ? 's' : ''}` : '';
                    return (
                      <option key={user.id} value={user.id}>
                        {formatUserName(user)} ({user.role}){workloadLabel}
                      </option>
                    );
                  })}
                </select>
                {responsableLine === 'primary' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Seuls les utilisateurs avec le rôle "bureau" ou "admin" peuvent être responsables principaux
                  </p>
                )}
                {responsableLine === 'second' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Le changement manuel remplacera la valeur synchronisée automatiquement avec le RV
                  </p>
                )}
              </div>

              {/* Notes (optional) */}
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (optionnel)
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Raison du changement de responsable..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={3}
                  disabled={isChanging}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ces notes seront enregistrées dans l'historique pour assurer la transparence
                </p>
              </div>

              {/* Error Message */}
              {changeError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">
                    Erreur: {changeError}
                  </p>
                </div>
              )}

              {/* Warning about history */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  ⚠️ Ce changement sera enregistré dans l'historique et sera visible par tous les utilisateurs ayant accès au dossier.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="tracker-modal-footer">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isChanging}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isChanging || !selectedUserId || isLoadingUsers}
          >
            {isChanging ? 'Enregistrement...' : 'Changer le responsable'}
          </Button>
        </div>
      </div>
    </>
  );
};
